import { useQuery } from '@tanstack/react-query';
import axiosInstance from '../api/axiosInstance';

interface Subordinate {
  user_id: string;
  name: string;
  avatar_url: string | null;
}

interface ManagerViewer {
  user_id: string;
  name: string;
  avatar_url: string | null;
  relation: string;
}

export const managerHierarchyKeys = {
  all: ['manager-hierarchy'] as const,
  subordinates: (maxDepth?: number) =>
    [...managerHierarchyKeys.all, 'subordinates', maxDepth] as const,
  hasSubordinates: () =>
    [...managerHierarchyKeys.all, 'has-subordinates'] as const,
  viewers: (ownerId: string, module: string) =>
    [...managerHierarchyKeys.all, 'viewers', ownerId, module] as const,
};

const BASE_URL = '/api/v1/manager-hierarchy';

export function useSubordinates(maxDepth = 2) {
  return useQuery({
    queryKey: managerHierarchyKeys.subordinates(maxDepth),
    queryFn: async (): Promise<Subordinate[]> => {
      const response = await axiosInstance.get(`${BASE_URL}/subordinates`, {
        params: { max_depth: maxDepth },
      });
      return response.data.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useHasSubordinates() {
  return useQuery({
    queryKey: managerHierarchyKeys.hasSubordinates(),
    queryFn: async (): Promise<boolean> => {
      const response = await axiosInstance.get(`${BASE_URL}/has-subordinates`);
      return response.data.data.has_subordinates;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useManagerViewers(
  ownerId: string | undefined,
  module = 'pipeline'
) {
  return useQuery({
    queryKey: managerHierarchyKeys.viewers(ownerId ?? '', module),
    queryFn: async (): Promise<{
      data: ManagerViewer[];
      enabled: boolean;
    }> => {
      const response = await axiosInstance.get(`${BASE_URL}/viewers`, {
        params: { owner_id: ownerId, module },
      });
      return response.data;
    },
    enabled: !!ownerId,
    staleTime: 5 * 60 * 1000,
  });
}
